'use client';

import { useSidebarNavigationOptional, type CandidateSearchFilters } from '@components/contexts/SidebarNavigationContext';
import { getI18nLinkHref } from '@services/i18nPaths';
import { PromptBarShell } from './PromptBarShell';
import { PromptBarActionsPopup } from './PromptBarActionsPopup';
import { PromptBarChips } from './PromptBarChips';
import { SearchBranch } from './SearchBranch';
import { ChatBranch } from './ChatBranch';
import { MissionChip } from './MissionChip';
import { MissionChipsRow } from './MissionChipsRow';
import { useCurrentMission } from './useCurrentMission';
import { usePromptBarMode, type PromptBarMode } from './usePromptBarMode';

interface CandidatePromptBarProps {
  locale: Locale;
  isAuthenticated?: boolean;
  /** Force a mode. Otherwise derived from currentView via usePromptBarMode. */
  modeOverride?: PromptBarMode;
  /** Chat submit handler. Required for chat mode to function; if absent the
   *  branch still renders but the user cannot send a message. */
  onChatSubmit?: (text: string) => Promise<void>;
  /** Search submit handler. Defaults to navigateToCandidateSearch. */
  onSearchSubmit?: (filters: CandidateSearchFilters) => void;
  /** Render in document flow instead of fixed-position overlay. */
  inline?: boolean;
  /** Render just the mission chips instead of the full prompt bar. Used on
   *  surfaces where the input is not useful (company pages). */
  collapsedFab?: boolean;
}

const noopChatSubmit: (text: string) => Promise<void> = async () => {};

export const CandidatePromptBar = ({
  locale,
  isAuthenticated = false,
  modeOverride,
  onChatSubmit,
  onSearchSubmit,
  inline = false,
  collapsedFab = false,
}: CandidatePromptBarProps) => {
  const mode = usePromptBarMode(modeOverride);
  const sidebarNav = useSidebarNavigationOptional();
  const handleSearchSubmit = onSearchSubmit ?? sidebarNav?.navigateToCandidateSearch;
  const mission = useCurrentMission(locale);

  // Resetting the current mission (the chip's ✕) returns to the job-search home, regardless of
  // context (Add Job and other missions are reached from the menu, not by resetting).
  const resetHref = getI18nLinkHref('/mc/search', locale);

  // Company-page surfaces (collapsedFab): just the mission chips — no full prompt bar.
  if (collapsedFab) {
    return <PromptBarChips locale={locale} mode={mode} isAuthenticated={isAuthenticated} inline={inline} />;
  }

  const popup = <PromptBarActionsPopup locale={locale} mode={mode} isAuthenticated={isAuthenticated} />;
  const chip = <MissionChip label={mission.label} href={resetHref} />;

  return (
    <div className="w-full">
      <PromptBarShell
        inline={inline}
        belowSlot={<MissionChipsRow locale={locale} mode={mode} isAuthenticated={isAuthenticated} className="mt-2" />}
      >
        {mode === 'search' && handleSearchSubmit
          ? <SearchBranch locale={locale} onSubmit={handleSearchSubmit} leftSlot={popup} missionChip={chip} />
          : <ChatBranch onSubmit={onChatSubmit ?? noopChatSubmit} leftSlot={popup} missionChip={chip} />}
      </PromptBarShell>
    </div>
  );
};
